import { Icon, Menu, MenuItem, Position } from "@blueprintjs/core"
import { useToggle } from "@mantine/hooks"
import CustomButton from "components/Atoms/CustomButton"
import CustomCard from "components/Atoms/CustomCard"
import CustomDialog from "components/Atoms/CustomDialog"
import CustomPopover from "components/Atoms/CustomPopover"
import StyledLink from "components/Atoms/StyledLink"
import Error from "components/Common/Error"
import TablePagination from "components/Molecules/TablePagination"
import NewResourceButton from "components/Organisms/AddResourceButton"
import { Subtitle, Title } from "components/Organisms/table/DataCell"
import SerialColumn from "components/Organisms/table/SerialColumn"
import CustomReactTable from "components/Templates/CustomReactTable"
import ModuleHeader, { Description } from "components/Templates/ModuleHeader"
import dayjs from "dayjs"
import relativeTime from "dayjs/plugin/relativeTime"
import {
  useNotificationTriggerListQuery,
  useRehitNotificationMutation,
} from "generated/graphql"
import { useSearchFilter } from "hooks/searchFilterHooks"
import React from "react"
import { useNavigate } from "react-router-dom"
import AppToaster from "utils/AppToaster"
dayjs.extend(relativeTime)

const NewNotificationTriggers = () => {
  const [{ search, page }, setFilter] = useSearchFilter()
  const { data, loading, error, refetch } = useNotificationTriggerListQuery({
    variables: {
      query: search,
      first: 15,
      skip: page * 15,
    },
  })
  const COLUMNS = [
    SerialColumn,
    {
      accessor: "actions",
      maxWidth: 55,
      Cell({ original }) {
        const navigate = useNavigate()
        const [isOpen, setOpen] = useToggle()
        const [rehit, { loading }] = useRehitNotificationMutation({
          onCompleted() {
            AppToaster.show({
              intent: "success",
              icon: "tick",
              message: "Notification re-triggered successfully",
            })
            refetch()
            setOpen(false)
          },
          onError() {
            AppToaster.show({
              intent: "danger",
              icon: "error",
              message: "Unable to re-trigger the notification",
            })
            setOpen(false)
          },
        })
        return (
          <>
            <CustomPopover
              position={Position.BOTTOM_LEFT}
              minimal={true}
              content={
                <Menu>
                  <MenuItem
                    disabled={loading}
                    text="Re-trigger"
                    icon="refresh"
                    onClick={() => {
                      setOpen(true)
                    }}
                  />
                  <MenuItem
                    text="View Template"
                    icon="info-sign"
                    onClick={() => {
                      navigate(
                        `/d/registration/notification-templates/${original.template.id}`
                      )
                    }}
                  />
                </Menu>
              }
            >
              <CustomButton
                style={{ transform: "rotate(90deg)" }}
                icon="more"
                minimal={true}
              />
            </CustomPopover>
            <CustomDialog
              onClose={() => setOpen(false)}
              light
              isOpen={isOpen}
              title="Re-trigger Notification"
              footerRightButton={
                <CustomButton
                  text="Submit"
                  dark
                  icon="endorsed"
                  loading={loading}
                  onClick={() => {
                    rehit({
                      variables: {
                        triggerId: original.id,
                      },
                    })
                  }}
                />
              }
            >
              <p>
                This trigger will send notifications to{" "}
                <b>{original.usersCount}</b> users, are you sure you want to
                proceed?{" "}
              </p>
            </CustomDialog>
          </>
        )
      },
    },
    {
      width: 250,
      Header: "Template",
      accessor: "template",
      Cell({ value, original }) {
        if (!value?.id) return "---"
        return (
          <>
            <StyledLink to={`/d/registration/notifications/${original.id}`}>
              {value?.name}
            </StyledLink>
          </>
        )
      },
    },

    {
      width: 150,
      Header: "Provider",
      accessor: "provider",
      Cell({ value }) {
        return (
          <div>
            <Title>{value.name}</Title>
            <Subtitle>{value.kind}</Subtitle>
          </div>
        )
      },
    },
    {
      width: 150,
      Header: "Status",
      accessor: "isTriggered",
      Cell({ value }) {
        return (
          <Icon
            icon={value ? "endorsed" : "cross"}
            intent={value ? "success" : "danger"}
          />
        )
      },
    },
    {
      width: 300,
      Header: "Broadcast Time",
      accessor: "broadcastTime",
      Cell({ value }) {
        if (!value) return "---"
        return (
          <div>
            <Title>{dayjs(value).fromNow()}</Title>
            <Subtitle>{dayjs(value).format("Do MMM YY hh:mm a")}</Subtitle>
          </div>
        )
      },
    },
    {
      Header: "Total Users",
      accessor: "usersCount",
      Cell({ value }) {
        return <>{value?.toLocaleString()}</>
      },
    },
    {
      Header: "Subject",
      accessor: "title",
    },
  ]
  if (error) return <Error message={error?.name} />

  return (
    <CustomCard className="p-0 m-3">
      <ModuleHeader
        borderBottom="none"
        query={search ?? ""}
        onQueryChange={search => {
          setFilter(prev => ({
            ...prev,
            search,
            page: 0,
          }))
        }}
        white
        className="p-3"
        header={
          <Description icon="notifications" title="Notification Triggers" />
        }
      >
        <TablePagination
          className="mr-3"
          page={page}
          onPageChange={page => {
            setFilter(prev => ({
              ...prev,
              page,
            }))
          }}
        />

        <NewResourceButton text={"Trigger"} module="notifications" />
      </ModuleHeader>
      <CustomReactTable
        leftAlign={true}
        loading={loading}
        loadingText="Loading notification triggers"
        pageSize={13}
        data={data?.allNotificationTriggers}
        columns={COLUMNS}
      />
    </CustomCard>
  )
}

export default NewNotificationTriggers
